import { z } from 'zod';
import { apiClient } from '../lib/api';
import { buildApiUrl } from '../utils/apiUrl';
import {
	ApiResponseSchema,
	PaginatedApiResponseSchema,
} from '../schemas';
import { UniversalChartDataSchema, NivoLineChartDataSchema } from '../schemas/chartSchema';

export const FacultySchema = z.object({
	maKhoa: z.string(),
	tenKhoa: z.string(),
	doUuTien: z.number().int(),
	createdAt: z.string(),
	updatedAt: z.string(),
}).strip();

export const CreateFacultySchema = FacultySchema.pick({
	maKhoa: true,
	tenKhoa: true,
	doUuTien: true,
});

export const UpdateFacultySchema = FacultySchema.pick({
	tenKhoa: true,
	doUuTien: true,
});

export const FacultyYearStatsSchema = z.object({
	maKhoa: z.string(),
	tenKhoa: z.string(),
	yearData: z.record(z.string(), z.number())
}).strip();

export const FacultyStatsResponseSchema = z.object({
	years: z.array(z.number()),
	stats: z.array(FacultyYearStatsSchema)
}).strip();

export const FacultyOverviewStatsSchema = z.object({
	totalFaculties: z.number(),
	facultyDetails: z.array(z.object({
		maKhoa: z.string(),
		tenKhoa: z.string(),
		studentCount: z.number(),
		majorCount: z.number(),
		doUuTien: z.number()
	}).strip())
}).strip();

export const facultyApi = {
	// Get all faculties with pagination support
	getFaculties: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/faculties', params);
		const response = await apiClient.get(url);
		return PaginatedApiResponseSchema(FacultySchema.array()).parse(response.data);
	},

	// CRUD operations for faculties
	createFaculty: async (data: z.infer<typeof CreateFacultySchema>) => {
		const response = await apiClient.post('/faculties', data);
		return ApiResponseSchema(FacultySchema).parse(response.data);
	},

	updateFaculty: async (id: string, data: z.infer<typeof UpdateFacultySchema>) => {
		const response = await apiClient.put(`/faculties/${id}`, data);
		return ApiResponseSchema(FacultySchema).parse(response.data);
	},

	deleteFaculty: async (id: string): Promise<{ message: string }> => {
		const response = await apiClient.delete<{ message: string }>(`/faculties/${id}`);
		return response.data;
	},

	getFacultyStats: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/faculties/stats/admission', params);
		const response = await apiClient.get(url);
		return PaginatedApiResponseSchema(FacultyStatsResponseSchema).parse(response.data);
	},

	// Get faculty overview stats
	getFacultyOverview: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/faculties/stats/overview', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(FacultyOverviewStatsSchema).parse(response.data);
	},

	// Get faculty chart statistics
	getFacultyChartStats: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/faculties/stats/chart-data', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(UniversalChartDataSchema).parse(response.data);
	},


	// Get Nivo line chart data for faculties
	getNivoLineChartData: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/faculties/stats/nivo-chart-data', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(NivoLineChartDataSchema).parse(response.data);
	},

	// Export faculties to Excel
	exportToExcel: async (params?: URLSearchParams): Promise<void> => {
		return apiClient.exportToExcel('/faculties/export/excel', params);
	},
};